import { clamp } from "../utils/math";
import type { RngStreams } from "../utils/random";
import { createSmoothed, snapSmoothed } from "../utils/smoothing";
import type { NoteEvent, ScheduledEvent } from "../scheduling/ScheduledEvent";
import { stepConductor } from "./Conductor";
import { mapGenreToFeatures, normaliseGenre } from "./GenreMapper";
import {
  MAX_EVENTS_PER_ROLE,
  patternHash,
  stepRolePattern,
  type RhythmContext,
} from "./RhythmEngine";
import { performEvent, shouldFill } from "./PerformanceEngine";
import { createSectionState, stepSection } from "./SectionManager";
import {
  RHYTHM_ROLES,
  type BarReport,
  type GenreBlendInput,
  type MusicState,
  type RhythmRole,
  type RhythmState,
} from "./musicTypes";

/**
 * One bar of music, end to end.
 *
 * Pure with respect to audio: it takes state and returns events, so 500 bars
 * can be simulated in a test without Web Audio. AudioEngine calls it once per
 * bar and hands the events to the queue.
 */

/** Phase 1A has no Music panel; these are the working defaults. */
export const PHASE_1A_DEFAULTS = {
  rhythmicComplexity: 0.45,
  grooveStrength: 0.6,
  silenceProbability: 0.12,
  humanization: 0.5,
} as const;

export interface MusicEngineInputs {
  genre: GenreBlendInput;
  energy: number;
  density: number;
}

function createRhythmState(): RhythmState {
  const state = {} as RhythmState;
  for (const role of RHYTHM_ROLES) {
    state[role] = {
      pattern: null,
      barsSinceMutation: 0,
      restBarsRemaining: 0,
      recentHashes: [],
    };
  }
  return state;
}

export function createMusicState(streams: RngStreams, inputs: MusicEngineInputs): MusicState {
  const genre = normaliseGenre(inputs.genre);
  return {
    bar: 0,
    section: createSectionState(streams.section),
    // Genre and density move over bars; see docs 4.2 route 3.
    energy: createSmoothed(inputs.energy, 3),
    density: createSmoothed(inputs.density, 3),
    rhythmicComplexity: createSmoothed(PHASE_1A_DEFAULTS.rhythmicComplexity, 4),
    grooveStrength: createSmoothed(PHASE_1A_DEFAULTS.grooveStrength, 4),
    silenceProbability: createSmoothed(PHASE_1A_DEFAULTS.silenceProbability, 4),
    genre,
    features: mapGenreToFeatures({
      genre,
      grooveStrength: PHASE_1A_DEFAULTS.grooveStrength,
      rhythmicComplexity: PHASE_1A_DEFAULTS.rhythmicComplexity,
    }),
    rhythm: createRhythmState(),
    barsSinceFill: 99,
    lastBarReport: {
      bar: 0,
      eventCount: 0,
      activeRoles: [],
      averageVelocity: 0,
      averageTimingOffsetMs: 0,
      patternHash: "-",
      wasFill: false,
    },
  };
}

export function resetMusicState(
  state: MusicState,
  streams: RngStreams,
  inputs: MusicEngineInputs,
): void {
  state.bar = 0;
  state.section = createSectionState(streams.section);
  snapSmoothed(state.energy, inputs.energy);
  snapSmoothed(state.density, inputs.density);
  snapSmoothed(state.rhythmicComplexity, PHASE_1A_DEFAULTS.rhythmicComplexity);
  snapSmoothed(state.grooveStrength, PHASE_1A_DEFAULTS.grooveStrength);
  snapSmoothed(state.silenceProbability, PHASE_1A_DEFAULTS.silenceProbability);
  state.genre = normaliseGenre(inputs.genre);
  state.rhythm = createRhythmState();
  state.barsSinceFill = 99;
}

/**
 * Decides which roles play this bar.
 *
 * Roles rest: everything sounding all the time is the surest way to sound
 * mechanical. Density and the section decide how much of the kit is in play.
 */
function activeRoles(state: MusicState, streams: RngStreams): RhythmRole[] {
  const density = state.density.current;
  const active: RhythmRole[] = [];

  for (const role of RHYTHM_ROLES) {
    const roleState = state.rhythm[role];
    if (roleState.restBarsRemaining > 0) {
      roleState.restBarsRemaining -= 1;
      continue;
    }

    // Entry thresholds: the kick is the backbone, accents are occasional colour.
    const threshold: Record<RhythmRole, number> = {
      kick: 0.08,
      pulse: 0.15,
      percussion: 0.42,
      accent: 0.55,
    };
    const silence = state.silenceProbability.current + state.features.silenceTendency * 0.4;
    if (density < threshold[role]) continue;

    // Occasional rest, more likely in sparse/ambient settings.
    const restChance = clamp(silence * 0.22, 0, 0.4);
    if (streams.texture.chance(restChance)) {
      roleState.restBarsRemaining = streams.texture.int(1, role === "kick" ? 1 : 3);
      continue;
    }
    active.push(role);
  }

  // A bar with nothing at all is a legitimate ambient outcome, but only rarely.
  return active;
}

export interface BarPlanContext {
  ticksPerBar: number;
  idBase: number;
  clickEnabled: boolean;
}

export interface BarPlanResult {
  events: ScheduledEvent[];
  report: BarReport;
  /** Ids consumed, so the caller can keep its counter monotonic. */
  idsUsed: number;
}

/**
 * Advances the music one bar and returns the events for it.
 *
 * Order matters: genre features first (they shape everything), then section,
 * then the conductor's scalars, then rhythm, then performance.
 */
export function planBar(
  state: MusicState,
  streams: RngStreams,
  inputs: MusicEngineInputs,
  ctx: BarPlanContext,
): BarPlanResult {
  state.bar += 1;

  state.genre = normaliseGenre(inputs.genre);
  state.features = mapGenreToFeatures({
    genre: state.genre,
    grooveStrength: state.grooveStrength.current,
    rhythmicComplexity: state.rhythmicComplexity.current,
  });

  const sectionChanged = stepSection(state.section, streams.section);
  const isSectionEnd = state.section.bar >= state.section.length - 1;

  stepConductor(state, {
    energy: inputs.energy,
    density: inputs.density,
    rhythmicComplexity: PHASE_1A_DEFAULTS.rhythmicComplexity,
    grooveStrength: PHASE_1A_DEFAULTS.grooveStrength,
    silenceProbability: PHASE_1A_DEFAULTS.silenceProbability,
  });

  const isFill = shouldFill(
    state.barsSinceFill,
    isSectionEnd || sectionChanged,
    state.energy.current,
    state.features,
    streams.rare,
  );
  state.barsSinceFill = isFill ? 0 : state.barsSinceFill + 1;

  const rhythmCtx: RhythmContext = {
    features: state.features,
    density: state.density.current,
    rhythmicComplexity: state.rhythmicComplexity.current,
    grooveStrength: state.grooveStrength.current,
    silenceProbability: state.silenceProbability.current,
    isFill,
    rng: streams.rhythm,
  };

  const roles = activeRoles(state, streams);
  const events: ScheduledEvent[] = [];
  let nextId = ctx.idBase;
  const hashes: string[] = [];
  let velocitySum = 0;
  let offsetSum = 0;

  const barStartTicks = (state.bar - 1) * ctx.ticksPerBar;

  for (const role of roles) {
    const pattern = stepRolePattern(role, state.rhythm[role], rhythmCtx);
    hashes.push(`${role}=${patternHash(pattern)}`);

    const ticksPerStep = ctx.ticksPerBar / pattern.stepsPerBar;

    for (const step of pattern.steps) {
      if (events.length >= MAX_EVENTS_PER_ROLE * RHYTHM_ROLES.length) break;

      const isAccent = pattern.accents.includes(step);
      const isGhost = pattern.ghosts.includes(step) && !isAccent;
      const performance = performEvent({
        role,
        isAccent,
        isGhost,
        step,
        stepsPerBar: pattern.stepsPerBar,
        energy: state.energy.current,
        features: state.features,
        humanization: PHASE_1A_DEFAULTS.humanization,
        rng: streams.performance,
      });

      const event: NoteEvent = {
        kind: "note",
        id: nextId,
        instrument: role,
        timeTicks: Math.round(barStartTicks + step * ticksPerStep),
        durationTicks: Math.max(1, Math.round(ticksPerStep * 0.5)),
        timingOffsetMs: performance.timingOffsetMs,
        midi: null,
        velocity: performance.velocity,
        dispatched: false,
      };
      nextId += 1;
      velocitySum += performance.velocity;
      offsetSum += performance.timingOffsetMs;
      events.push(event);
    }
  }

  if (ctx.clickEnabled) {
    events.push({
      kind: "note",
      id: nextId,
      instrument: "diagnosticClick",
      timeTicks: barStartTicks,
      durationTicks: Math.max(1, Math.round(ctx.ticksPerBar / 32)),
      timingOffsetMs: 0,
      midi: 84,
      velocity: 0.25,
      dispatched: false,
    });
    nextId += 1;
  }

  const noteCount = events.length - (ctx.clickEnabled ? 1 : 0);
  const report: BarReport = {
    bar: state.bar,
    eventCount: noteCount,
    activeRoles: roles,
    averageVelocity: noteCount > 0 ? velocitySum / noteCount : 0,
    averageTimingOffsetMs: noteCount > 0 ? offsetSum / noteCount : 0,
    patternHash: hashes.join(" "),
    wasFill: isFill,
  };
  state.lastBarReport = report;

  events.sort((a, b) => a.timeTicks - b.timeTicks);

  return { events, report, idsUsed: nextId - ctx.idBase };
}
